/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import React from 'react';
import { arrayOf, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import NoImageIcon from './NoImageIcon';
import css from './ResponsiveImage.module.css';
import { fetchCurrentUserListingsRequest } from '../../containers/ListingPage/ListingPage.duck';

const ResponsiveImage = props => {
  const {
    className = null,
    rootClassName = null,
    alt,
    noImageMessage = null,
    image = null,
    listing,
    isRequirement,
    variants,
    listingDeleted,
    isCustomer,
    registration,
    listingTitle,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  if (image == null || variants.length === 0) {
    const noImageClasses = classNames(
      rootClassName || fetchCurrentUserListingsRequest.root,
      css.noImageContainer,
      className
    );

    const isInboxBooking = listingTitle == 'Booking';
    const noImageMessageText = noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;
    const bookingMessageText = <FormattedMessage id="ResponsiveImage.bookingImage" />;
    const professionalNoImage = <FormattedMessage id="ResponsiveImage.professionalNoImage" />;
    const bookingOrListingDeletedText = (
      <FormattedMessage id="ResponsiveImage.bookingListingDeleted" />
    );

    return (
      <div className={noImageClasses}>
        <div className={css.noImageWrapper}>
          {!registration && isRequirement && (
            <NoImageIcon className={css.noImageIcon} isRequirement={isRequirement} />
          )}
          {isRequirement || (isCustomer && !listingDeleted) || isInboxBooking ? (
            <div className={css.noImageText}>{bookingMessageText}</div> // shows 'booking' text
          ) : listingDeleted ? (
            <div className={css.noImageText}>{bookingOrListingDeletedText}</div> // shows 'Booking deleted' for inbox purposes
          ) : registration || isRequirement ? (
            <div className={css.noImageText}>{professionalNoImage}</div> // shows 'communication professional' for when use has no photo
          ) : (
            <div className={css.noImageText}>{noImageMessageText}</div> // shows ' ' for when there is no image
          )}
        </div>
      </div>
    );
  }

  const imageVariants = image.attributes.variants;

  const srcSet = variants
    .map(variantName => {
      const variant = imageVariants[variantName];

      if (!variant) {
        // Variant not available (most like just not loaded yet)
        return null;
      }
      return `${variant.url} ${variant.width}w`;
    })
    .filter(v => v != null)
    .join(', ');

  const imgProps = {
    className: classes,
    srcSet,
    ...rest,
  };

  return <img alt={alt} {...imgProps}/>;
};

ResponsiveImage.propTypes = {
  className: string,
  rootClassName: string,
  alt: string.isRequired,
  image: oneOfType([propTypes.image, propTypes.imageAsset]),
  variants: arrayOf(string).isRequired,
  noImageMessage: string,
};

export default ResponsiveImage;
